import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, InputNumber, message, Card, Typography, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { TestItem, TestItemType, TestItemPriority, TestItemSeverity, TestItemStatus, TestItemCreate, Folder } from '../../types/testItem';
import { testItemService } from '../../services/testItemService';
import { DragOutlined } from '@ant-design/icons';
import { TestDataList } from './TestDataList';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

interface TestItemFormProps {
    projectId: number;
    initialValues?: Partial<TestItem>;
    folders: Folder[];
    onSubmit: (values: TestItemCreate) => Promise<void>;
    onCancel: () => void;
    loading?: boolean;
    onDataChange?: () => void;
}

export const TestItemForm: React.FC<TestItemFormProps> = ({
    projectId,
    initialValues,
    folders,
    onSubmit,
    onCancel,
    loading: propLoading,
    onDataChange
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [type, setType] = useState<TestItemType>(initialValues?.type || TestItemType.TEST_CASE);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialValues) {
            const formattedValues = {
                ...initialValues,
                steps: initialValues.steps?.map(step => ({
                    title: step.action,
                    description: '',
                    expected_result: step.expected_result
                })),
                check_items: initialValues.check_items?.map(item => ({
                    title: item.description,
                    description: ''
                }))
            };
            form.setFieldsValue(formattedValues);
            if (initialValues.type) {
                setType(initialValues.type);
            }
        } else {
            form.resetFields();
            setType(TestItemType.TEST_CASE);
        }
    }, [initialValues, form]);

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);
            const currentVersion = initialValues?.version || 0;
            const formData = {
                project_id: projectId,
                title: values.title,
                description: values.description || '',
                type: values.type,
                priority: values.priority,
                severity: values.severity,
                status: values.status,
                folder_id: values.folder_id || null,
                estimate_minutes: values.estimate_minutes || 0,
                components: Array.isArray(values.components) ? values.components : [],
                labels: Array.isArray(values.labels) ? values.labels : [],
                steps: values.type === TestItemType.TEST_CASE ? (values.steps || []).map((step: any) => ({
                    action: step.title,
                    expected_result: step.expected_result,
                    test_data: step.test_data || '',
                    attachment_ids: step.attachment_ids || [],
                    test_data_ids: step.test_data_ids || []
                })) : undefined,
                check_items: values.type === TestItemType.CHECKLIST ? (values.check_items || []).map((item: any) => ({
                    description: item.title,
                    attachment_ids: item.attachment_ids || [],
                    test_data_ids: item.test_data_ids || []
                })) : undefined,
                version: currentVersion + 1,
                ...(initialValues?.created_by && { created_by: initialValues.created_by })
            };
            await onSubmit(formData);
            form.resetFields();
        } catch (error) {
            console.error('Form submission error:', error);
            message.error(t('testItems.errors.saveFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        form.resetFields();
        setType(TestItemType.TEST_CASE);
    };

    const renderStepFields = (name: number, restField: any, index: number, remove: (index: number) => void) => (
        <div
            key={`step-${name}`}
            style={{
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fff',
            }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <DragOutlined style={{ cursor: 'move' }} />
                        <Title level={5} style={{ margin: 0 }}>{t('testItems.form.step', { number: index + 1 })}</Title>
                    </Space>
                    <Button type="link" danger onClick={() => remove(name)}>
                        {t('testItems.form.removeStep')}
                    </Button>
                </div>
                <Form.Item
                    name={[name, 'title']}
                    label={t('testItems.form.stepTitle')}
                    rules={[{ required: true, message: t('testItems.errors.required') }]}
                >
                    <Input placeholder={t('testItems.form.stepTitlePlaceholder')} />
                </Form.Item>
                <Form.Item
                    name={[name, 'expected_result']}
                    label={t('testItems.form.expectedResult')}
                    rules={[{ required: true, message: t('testItems.errors.required') }]}
                >
                    <TextArea rows={2} placeholder={t('testItems.form.expectedResultPlaceholder')} />
                </Form.Item>
            </Space>
        </div>
    );

    const renderCheckItemFields = (name: number, restField: any, index: number, remove: (index: number) => void) => (
        <div
            key={`check-item-${name}`}
            style={{
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fff',
            }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                        <DragOutlined style={{ cursor: 'move' }} />
                        <Title level={5} style={{ margin: 0 }}>{t('testItems.form.checkItem', { number: index + 1 })}</Title>
                    </Space>
                    <Button type="link" danger onClick={() => remove(name)}>
                        {t('testItems.form.removeCheckItem')}
                    </Button>
                </div>
                <Form.Item
                    name={[name, 'title']}
                    label={t('testItems.form.checkItemTitle')}
                    rules={[{ required: true, message: t('testItems.errors.required') }]}
                >
                    <Input placeholder={t('testItems.form.checkItemTitlePlaceholder')} />
                </Form.Item>
                <Form.Item
                    name={[name, 'description']}
                    label={t('testItems.form.checkItemDescription')}
                >
                    <TextArea rows={2} placeholder={t('testItems.form.checkItemDescriptionPlaceholder')} />
                </Form.Item>
            </Space>
        </div>
    );

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={initialValues}
        >
            <Form.Item
                name="type"
                label={t('testItems.form.type')}
                rules={[{ required: true, message: t('testItems.form.typeRequired') }]}
            >
                <Select onChange={(value) => setType(value)}>
                    <Option value={TestItemType.TEST_CASE}>{t('testItems.types.testCase')}</Option>
                    <Option value={TestItemType.CHECKLIST}>{t('testItems.types.checklist')}</Option>
                </Select>
            </Form.Item>

            <Form.Item
                name="title"
                label={t('testItems.form.title')}
                rules={[{ required: true, message: t('testItems.form.titleRequired') }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name="description"
                label={t('testItems.form.description')}
            >
                <TextArea rows={4} />
            </Form.Item>

            <Form.Item
                name="folder_id"
                label={t('testItems.form.folder')}
            >
                <Select
                    allowClear
                    placeholder={t('testItems.form.selectFolder')}
                    showSearch
                    optionFilterProp="children"
                >
                    {folders.map(folder => (
                        <Option key={folder.id} value={folder.id}>
                            {folder.name}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="priority"
                label={t('testItems.form.priority')}
                rules={[{ required: true, message: t('testItems.form.priorityRequired') }]}
            >
                <Select>
                    <Option value={TestItemPriority.HIGH}>{t('testItems.priorities.high')}</Option>
                    <Option value={TestItemPriority.MEDIUM}>{t('testItems.priorities.medium')}</Option>
                    <Option value={TestItemPriority.LOW}>{t('testItems.priorities.low')}</Option>
                </Select>
            </Form.Item>

            <Form.Item
                name="severity"
                label={t('testItems.form.severity')}
                rules={[{ required: true, message: t('testItems.form.severityRequired') }]}
            >
                <Select>
                    <Option value={TestItemSeverity.BLOCKER}>{t('testItems.severities.blocker')}</Option>
                    <Option value={TestItemSeverity.MAJOR}>{t('testItems.severities.major')}</Option>
                    <Option value={TestItemSeverity.MINOR}>{t('testItems.severities.minor')}</Option>
                </Select>
            </Form.Item>

            <Form.Item
                name="status"
                label={t('testItems.form.status')}
                rules={[{ required: true, message: t('testItems.form.statusRequired') }]}
            >
                <Select>
                    <Option value={TestItemStatus.DRAFT}>{t('testItems.statuses.draft')}</Option>
                    <Option value={TestItemStatus.READY}>{t('testItems.statuses.ready')}</Option>
                    <Option value={TestItemStatus.REVIEWED}>{t('testItems.statuses.reviewed')}</Option>
                    <Option value={TestItemStatus.OBSOLETE}>{t('testItems.statuses.obsolete')}</Option>
                    <Option value={TestItemStatus.DEPRECATED}>{t('testItems.statuses.deprecated')}</Option>
                </Select>
            </Form.Item>

            <Form.Item
                name="estimate_minutes"
                label={t('testItems.form.estimate')}
            >
                <InputNumber min={0} />
            </Form.Item>

            <Form.Item
                name="components"
                label={t('testItems.form.components')}
            >
                <Form.List name="components">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                    <Form.Item
                                        name={name}
                                        rules={[{ required: true, message: t('testItems.errors.required') }]}
                                    >
                                        <Input placeholder={t('testItems.form.components')} />
                                    </Form.Item>
                                    <Button type="link" onClick={() => remove(name)}>
                                        {t('testItems.form.removeComponent')}
                                    </Button>
                                </Space>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block>
                                    {t('testItems.form.addComponent')}
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            <Form.Item
                name="labels"
                label={t('testItems.form.labels')}
            >
                <Form.List name="labels">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                    <Form.Item
                                        name={name}
                                        rules={[{ required: true, message: t('testItems.errors.required') }]}
                                    >
                                        <Input placeholder={t('testItems.form.labels')} />
                                    </Form.Item>
                                    <Button type="link" onClick={() => remove(name)}>
                                        {t('testItems.form.removeLabel')}
                                    </Button>
                                </Space>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block>
                                    {t('testItems.form.addLabel')}
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </Form.Item>

            {type === TestItemType.TEST_CASE ? (
                <Card title={t('testItems.form.steps')} style={{ marginBottom: '24px' }}>
                    <Form.List name="steps">
                        {(fields, { add, remove, move }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    renderStepFields(name, restField, index, remove)
                                ))}
                                <Button type="dashed" onClick={() => add()} block style={{ marginTop: '16px' }}>
                                    {t('testItems.form.addStep')}
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>
            ) : (
                <Card title={t('testItems.form.checkItems')} style={{ marginBottom: '24px' }}>
                    <Form.List name="check_items">
                        {(fields, { add, remove, move }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    renderCheckItemFields(name, restField, index, remove)
                                ))}
                                <Button type="dashed" onClick={() => add()} block style={{ marginTop: '16px' }}>
                                    {t('testItems.form.addCheckItem')}
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>
            )}

            <Divider>Test Data</Divider>
            {initialValues?.id && (
                <TestDataList
                    testItemId={initialValues.id}
                    folderId={initialValues.folder_id}
                    onDataChange={onDataChange}
                />
            )}

            <Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {t('common.save')}
                    </Button>
                    <Button onClick={handleReset}>
                        {t('common.reset')}
                    </Button>
                    <Button onClick={onCancel}>
                        {t('common.cancel')}
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default TestItemForm; 